import { migrationApis } from '@gleamer/apis';
import { Migration } from '@gleamer/types';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { useAuthorization } from './useImports';

const {
  fetchMigrationCatchUpChanges,
  fetchMigrationChanges,
  fetchMigrationCounts,
  fetchMigrationErrorGroups,
  fetchMigrationErrors,
  fetchMigrationGroups,
  fetchMigrationRun,
  fetchMigrationRuns,
  fetchMigrationSegments,
  fetchMigrationSources,
  sendMigrationCommand,
} = migrationApis;

type MigrationCatchUpChangesQuery = Migration.MigrationCatchUpChangesQuery;
type MigrationCommand = Migration.MigrationCommand;
type MigrationErrorsQuery = Migration.MigrationErrorsQuery;
type MigrationRun = Migration.MigrationRun;
type MigrationRunsQuery = Migration.MigrationRunsQuery;
type MigrationSegmentsQuery = Migration.MigrationSegmentsQuery;

/** While a run moves, the page asks whether it changed this often; one row read, so it can be frequent. */
export const MIGRATION_POLL_MS = 5_000;
/** A finished run is still asked, less often: a replay or a retry starts it again. */
export const MIGRATION_IDLE_POLL_MS = 30_000;

/** @param awaiting a run was just started and has not appeared yet: the list is read at the fast pace meanwhile */
export function useMigrationRuns(query: MigrationRunsQuery, awaiting = false) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-runs', query],
    queryFn: () => fetchMigrationRuns(auth, query),
    keepPreviousData: true,
    refetchInterval: data =>
      awaiting ||
      data?.items?.some(run => !Migration.isFinalRunState(run.state))
        ? MIGRATION_POLL_MS
        : MIGRATION_IDLE_POLL_MS,
  });
}

export function useMigrationGroups() {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-groups'],
    queryFn: () => fetchMigrationGroups(auth),
    staleTime: Infinity,
  });
}

/** What migration-service runs with: read again with the page, a redeploy may change it. */
export function useMigrationSources() {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-sources'],
    queryFn: () => fetchMigrationSources(auth),
  });
}

/** The run's page reads again: its details, segments, error groups, errors, counts and catch-up changes. */
function invalidateRun(queryClient: QueryClient, runId?: string) {
  [
    'migration-run',
    'migration-segments',
    'migration-error-groups',
    'migration-errors',
    'migration-counts',
    'migration-catch-up-changes',
  ].forEach(key =>
    queryClient.invalidateQueries({ queryKey: runId ? [key, runId] : [key] })
  );
}

/** Reads a run's page again at once — details, segments and errors — for its Refresh button. */
export function useRefreshMigrationRun(runId: string | undefined) {
  const queryClient = useQueryClient();
  return () => invalidateRun(queryClient, runId);
}

/** What the page shows of a run, to tell whether a run read later differs from it. */
function shown(run: MigrationRun) {
  return JSON.stringify([
    run.version,
    run.state,
    run.maxWorkers,
    run.segments,
    run.counters,
    run.unacknowledgedErrors,
    run.catchUp,
  ]);
}

/**
 * A run's details, read again only when something the page shows moved: the page polls `/changes` with the
 * mark it holds (a few aggregates), and a different mark invalidates the details, the segments and the errors,
 * which are larger reads. The first answer carries the run (there is no mark yet): when it differs from the
 * details — the run moved between the two reads — they are read again too.
 *
 * The poll runs while there are details to compare, also after a failed reload: the page keeps what it showed.
 */
export function useMigrationRun(runId: string | undefined) {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  const mark = useRef<string | undefined>(undefined);
  const details = useQuery({
    queryKey: ['migration-run', runId],
    queryFn: () => fetchMigrationRun(auth, runId),
    enabled: !!runId,
  });
  const final = details.data
    ? Migration.isFinalRunState(details.data.run.state)
    : false;
  useQuery({
    queryKey: ['migration-changes', runId],
    queryFn: () => fetchMigrationChanges(auth, runId, mark.current),
    enabled: !!runId && details.data !== undefined,
    refetchInterval: final ? MIGRATION_IDLE_POLL_MS : MIGRATION_POLL_MS,
    onSuccess: changes => {
      const moved =
        mark.current === undefined
          ? !!changes.run &&
            !!details.data &&
            shown(changes.run) !== shown(details.data.run)
          : !!changes.run;
      if (moved) {
        invalidateRun(queryClient, runId);
      }
      mark.current = changes.mark;
    },
  });
  return details;
}

export function useMigrationSegments(
  runId: string | undefined,
  query: MigrationSegmentsQuery
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-segments', runId, query],
    queryFn: () => fetchMigrationSegments(auth, runId, query),
    enabled: !!runId,
    keepPreviousData: true,
  });
}

/** A run's errors by kind and reason; read again with the rest of the page when the run's mark moves. */
export function useMigrationErrorGroups(runId: string | undefined) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-error-groups', runId],
    queryFn: () => fetchMigrationErrorGroups(auth, runId),
    enabled: !!runId,
    keepPreviousData: true,
  });
}

/**
 * A whole run's kinds counted side by side; read again with the rest of the page, and every half minute while a count is
 * due — the table's count lands after the run is over, when nothing else on the page moves.
 */
export function useMigrationCounts(runId: string | undefined) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-counts', runId],
    queryFn: () => fetchMigrationCounts(auth, runId),
    enabled: !!runId,
    keepPreviousData: true,
    refetchInterval: counts =>
      counts?.some(c => c.status === 'COUNTING') ? 30_000 : false,
  });
}

export function useMigrationErrors(
  runId: string | undefined,
  query: MigrationErrorsQuery
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-errors', runId, query],
    queryFn: () => fetchMigrationErrors(auth, runId, query),
    enabled: !!runId,
    keepPreviousData: true,
  });
}

/** The changes a run's catch-up recorded; read again with the rest of the page when the run's mark moves. */
export function useMigrationCatchUpChanges(
  runId: string | undefined,
  query: MigrationCatchUpChangesQuery
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['migration-catch-up-changes', runId, query],
    queryFn: () => fetchMigrationCatchUpChanges(auth, runId, query),
    enabled: !!runId,
    keepPreviousData: true,
  });
}

/**
 * Sends a command. Its effect is not in the answer — migration-service applies it a moment later — so what
 * the pages show is read again at once (a started run appears) and the run's poll picks up the rest.
 */
export function useMigrationCommand() {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: MigrationCommand) =>
      sendMigrationCommand(auth, command),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration-runs'] });
      invalidateRun(queryClient);
    },
  });
}
